import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { formatNumber } from '@angular/common';

/**
 * AI GENERATED - REVIEW, ADO 7841953 lines 1-87
 * Pipe to format negative monetary values using accounting notation (parentheses)
 * Story: #7841953 - Round Down not appearing as negative adjustment in UI
 * Uses Angular's formatNumber for proper locale-aware formatting
 */
@Pipe({
  name: 'negativeParentheses',
  standalone: false,
  pure: true,
})
export class NegativeValueParenthesesPipe implements PipeTransform {
  constructor(@Inject(LOCALE_ID) private locale: string) {}

  /**
   * Transforms a number to string with accounting notation for negative values.
   * Uses Angular's formatNumber (not Intl.NumberFormat) for consistent locale-aware formatting.
   *
   * Positive: 250.00 => "250.00"
   * Negative: -250.00 => "(250.00)"
   * Negative with thousands: -651104.01 => "(651,104.01)"
   * Edge case: -0.001 (rounds to 0.00) => "0.00" (zero treated as unsigned)
   *
   * @param value The number to format (number, string, null, or undefined)
   * @param digitsInfo Format: "1.2-2" (minIntegerDigits.minFractionDigits-maxFractionDigits)
   * @param style 'parentheses' (default) or 'minus' for negative formatting style
   * @param includeNegativeLabel If true, includes " (negative)" suffix for accessibility (default: false)
   * @returns Formatted string with parentheses for negative values, or null if value is invalid
   */
  transform(
    value: number | string | null | undefined,
    digitsInfo: string = '1.2-2',
    style: 'parentheses' | 'minus' = 'parentheses',
    includeNegativeLabel: boolean = false
  ): string | null {
    // Handle null/undefined/empty string
    if (value == null || value === '') {
      return null;
    }

    // Convert to number with strict validation
    let n: number;
    if (typeof value === 'string') {
      // Validate string is a complete valid number (not partial like '123abc')
      const trimmed = value.trim();
      if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
        return null;
      }
      n = parseFloat(trimmed);
    } else {
      n = Number(value);
    }

    // Check for NaN or Infinity
    if (isNaN(n) || !isFinite(n)) {
      return null;
    }

    // Determine if original value is negative (preserve sign for zero rounding case)
    const isNegative = n < 0;

    // Use Angular's formatNumber for locale-aware formatting
    const absValue = Math.abs(n);
    const text = formatNumber(absValue, this.locale, digitsInfo);

    // Check if the rounded value is effectively zero
    // Use numeric comparison: if the absolute value rounds to 0.00, treat as zero
    const roundedValue = parseFloat(text.replace(/[,\s]/g, ''));
    const isZero = roundedValue === 0;

    // For accounting notation: zero is always shown as unsigned (no parentheses)
    if (isZero) {
      return text;
    }

    // Return positive values unchanged
    if (!isNegative) {
      return text;
    }

    // Apply style for negative values
    let result: string;
    if (style === 'parentheses') {
      result = `(${text})`;
    } else {
      result = `-${text}`;
    }

    // Add accessibility label if requested (for aria-label usage)
    if (includeNegativeLabel) {
      result = `${result} (negative)`;
    }

    return result;
  }
}
